import api from './api';
import { auth } from './firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from 'firebase/auth';

const EMAIL_LINK_STORAGE_KEY = 'tlfr.emailForSignIn';

export const parseIdentifier = (input) => {
  const trimmed = String(input || '').trim();
  if (/^\S+@\S+\.\S+$/.test(trimmed)) {
    return { type: 'email', value: trimmed.toLowerCase() };
  }
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) {
    return { type: 'phone', phone: `+91${digits}`, rawDigits: digits };
  }
  if (digits.length === 11 && digits.startsWith('0')) {
    const raw = digits.slice(1);
    return { type: 'phone', phone: `+91${raw}`, rawDigits: raw };
  }
  if (digits.length === 12 && digits.startsWith('91')) {
    const raw = digits.slice(2);
    return { type: 'phone', phone: `+91${raw}`, rawDigits: raw };
  }
  if (digits.length >= 10 && digits.length <= 13) {
    return { type: 'phone', phone: `+${digits}`, rawDigits: digits };
  }
  return { type: 'invalid' };
};

export const identifierToAuthEmail = (identifier) => {
  const parsed = parseIdentifier(identifier);
  if (parsed.type === 'email') return parsed.value;
  if (parsed.type === 'phone') return `${parsed.rawDigits}@phone.toletforrent.com`;
  return String(identifier || '').trim();
};

const authService = {
  /**
   * Firebase sends the reset mail and hosts the reset page, so there is no
   * backend involvement and no token for us to store.
   *
   * Deliberately does not reveal whether the address exists — see the caller.
   */
  async sendPasswordReset(identifier) {
    const parsed = parseIdentifier(identifier);
    if (parsed.type === 'phone') {
      const err = new Error('Password reset via SMS is not supported. Please sign in with your password or contact support.');
      err.code = 'auth/phone-reset-unsupported';
      throw err;
    }
    const email = parsed.type === 'email' ? parsed.value : identifier;
    await sendPasswordResetEmail(auth, email);
  },

  // ─────────────────────────────────────────────────────────────
  // Email OR Mobile Number + password
  // ─────────────────────────────────────────────────────────────
  async loginWithIdentifier(identifier, password) {
    const parsed = parseIdentifier(identifier);
    if (parsed.type === 'invalid') {
      const err = new Error('Please enter a valid email address or 10-digit mobile number.');
      err.code = 'auth/invalid-identifier';
      throw err;
    }
    const authEmail = parsed.type === 'email' ? parsed.value : `${parsed.rawDigits}@phone.toletforrent.com`;
    const credential = await signInWithEmailAndPassword(auth, authEmail, password);
    const firebaseToken = await credential.user.getIdToken();
    return this.loginWithFirebase(firebaseToken);
  },

  async registerWithIdentifier(identifier, password) {
    const parsed = parseIdentifier(identifier);
    if (parsed.type === 'invalid') {
      const err = new Error('Please enter a valid email address or 10-digit mobile number.');
      err.code = 'auth/invalid-identifier';
      throw err;
    }
    const authEmail = parsed.type === 'email' ? parsed.value : `${parsed.rawDigits}@phone.toletforrent.com`;
    const credential = await createUserWithEmailAndPassword(auth, authEmail, password);
    const firebaseToken = await credential.user.getIdToken();
    return this.loginWithFirebase(firebaseToken);
  },

  async loginWithEmail(email, password) {
    return this.loginWithIdentifier(email, password);
  },

  async registerWithEmail(email, password) {
    return this.registerWithIdentifier(email, password);
  },

  // ─────────────────────────────────────────────────────────────
  // Google
  // ─────────────────────────────────────────────────────────────
  async loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const credential = await signInWithPopup(auth, provider);
    const firebaseToken = await credential.user.getIdToken();
    return this.loginWithFirebase(firebaseToken);
  },

  // ─────────────────────────────────────────────────────────────
  // Phone OTP
  //
  // Usage:
  //   const verifier = authService.getRecaptchaVerifier('recaptcha-container');
  //   const confirmation = await authService.sendPhoneOtp('+919876543210', verifier);
  //   ...user enters code...
  //   const result = await authService.verifyPhoneOtp(confirmation, code);
  // ─────────────────────────────────────────────────────────────
  getRecaptchaVerifier(containerId = 'recaptcha-container') {
    // Reuse if one is already mounted (Firebase will throw if you create twice)
    if (window.__tlfrRecaptcha) return window.__tlfrRecaptcha;
    const verifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
    });
    window.__tlfrRecaptcha = verifier;
    return verifier;
  },

  resetRecaptcha() {
    try {
      window.__tlfrRecaptcha?.clear();
    } catch {
      /* noop */
    }
    window.__tlfrRecaptcha = null;
  },

  /**
   * Send OTP to a phone number. Returns a `confirmationResult` to be passed
   * back into verifyPhoneOtp along with the user-entered code.
   * `phone` must be E.164 (e.g. +919876543210).
   */
  async sendPhoneOtp(phone, recaptchaVerifier) {
    return signInWithPhoneNumber(auth, phone, recaptchaVerifier);
  },

  async verifyPhoneOtp(confirmationResult, code) {
    const credential = await confirmationResult.confirm(code);
    const firebaseToken = await credential.user.getIdToken();
    return this.loginWithFirebase(firebaseToken);
  },

  // ─────────────────────────────────────────────────────────────
  // Email link (passwordless, Airbnb-style "send me a sign-in link")
  // ─────────────────────────────────────────────────────────────
  /**
   * Send a one-tap sign-in link to `email`.
   * `redirectUrl` is where the user lands after clicking the link.
   * Defaults to the current /user-registration-login page.
   */
  async sendEmailLink(email, redirectUrl) {
    const url = redirectUrl || `${window.location.origin}/user-registration-login`;
    await sendSignInLinkToEmail(auth, email, {
      url,
      handleCodeInApp: true,
    });
    // Persist email so the user can complete sign-in on the same device
    // even if they close and reopen the tab.
    window.localStorage.setItem(EMAIL_LINK_STORAGE_KEY, email);
  },

  /**
   * Returns true if the current URL is a sign-in email link.
   */
  isEmailLink(url = window.location.href) {
    return isSignInWithEmailLink(auth, url);
  },

  /**
   * Complete email-link sign-in. If `email` is not provided we read the one
   * saved by sendEmailLink. If still unavailable (e.g. user opened the link
   * on a different device), the caller must prompt for it.
   */
  async completeEmailLinkSignIn(email, url = window.location.href) {
    const storedEmail = email || window.localStorage.getItem(EMAIL_LINK_STORAGE_KEY);
    if (!storedEmail) {
      const err = new Error('Email required to complete sign-in.');
      err.code = 'auth/email-required-for-link';
      throw err;
    }
    const credential = await signInWithEmailLink(auth, storedEmail, url);
    window.localStorage.removeItem(EMAIL_LINK_STORAGE_KEY);
    const firebaseToken = await credential.user.getIdToken();
    return this.loginWithFirebase(firebaseToken);
  },

  // ─────────────────────────────────────────────────────────────
  // Bridge: Firebase ID token → backend JWT
  // ─────────────────────────────────────────────────────────────
  async loginWithFirebase(firebaseToken, fcmToken = null) {
    const { data } = await api.post('/auth/firebase-login', {
      firebaseToken,
      fcmToken,
    });
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    localStorage.setItem('isAuthenticated', 'true');
    localStorage.setItem(
      'userType',
      data.user?.role === 'owner' || data.user?.role === 'both' ? 'owner' : 'tenant',
    );
    return data;
  },

  logout() {
    firebaseSignOut(auth).catch(() => {});
    this.resetRecaptcha();
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userType');
    localStorage.removeItem('propertyListingDraft');
  },

  getToken() {
    return localStorage.getItem('accessToken');
  },

  getUser() {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  },

  isAuthenticated() {
    return !!localStorage.getItem('accessToken');
  },
};

export default authService;
